import { useEffect, useState } from 'react';
import { useAppDispatch, useAppState } from '@/store';
import { ICategory } from '@/models/categories.model';
import { categoriesSelector, getCategoryDetails, useCategoriesAutocomplete } from '@/modules/Categories';
import { TreeSelectDataNode } from '../helpers';
import { showErrorToast } from '@/components';
import { updateCategoryChildren } from '../helpers';
import { useTranslation } from 'react-i18next';

export const useCategoriesSelect = (initialCategoryParentId?: string) => {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();
  const { list: autocompleteCategories, onSearch } = useCategoriesAutocomplete({});
  const { categories: initialCategories } = useAppState(categoriesSelector);
  const [categories, setCategories] = useState<ICategory[] | null>(null);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());

  const updateInitialCategoriesList = async (categoriesList: ICategory[]) => {
    if (initialCategoryParentId) {
      // if this category is the last level category - need to load
      // all last level categories for its parent category
      const parentCategory = await dispatch(getCategoryDetails(initialCategoryParentId)).unwrap();
      const updatedCategories = updateCategoryChildren(
        categoriesList,
        initialCategoryParentId,
        parentCategory.sub_categories,
        t('no_subcategories_placeholder'),
      );
      setCategories(updatedCategories);
    } else {
      setCategories(categoriesList);
    }
  };

  const getCategoryChildren = async (categoryId: string): Promise<ICategory[]> => {
    const parentCategory = await dispatch(getCategoryDetails(categoryId)).unwrap();
    return parentCategory.sub_categories || [];
  };

  const handleExpandTreeItemData = async (node: TreeSelectDataNode) => {
    const { value: id } = node;
    if (expandedItems.has(id as string)) return;

    try {
      const subCategories = await getCategoryChildren(id as string);
      setExpandedItems((prev) => new Set(prev).add(id as string));

      setCategories((prevCategories) =>
        prevCategories
          ? updateCategoryChildren(prevCategories, id as string, subCategories, t('no_subcategories_placeholder'))
          : prevCategories,
      );
    } catch (error) {
      showErrorToast(t('subcategory_load_error'));
    }
  };

  useEffect(() => {
    if (initialCategories && !categories) {
      updateInitialCategoriesList(initialCategories);
    }
  }, [initialCategories, categories]);

  return { categories, expandedItems, handleExpandTreeItemData, autocompleteCategories, onSearch };
};
